import React, { ReactElement } from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CameraIcon from '../common/Icons/Camera';
import CameraDisabledIcon from '../common/Icons/CameraDisabled';
import MicrophoneIcon from '../common/Icons/Microphone';
import MicrophoneDisabledIcon from '../common/Icons/MicrophoneDisabled';
import ChatIcon from '../common/Icons/Chat';
import SwitchDeviceControl from '../SwitchDevice/SwitchDeviceControl';
import Tooltip from '../common/Tooltip';
import { CallRequestType } from '../../customer-call-sdk';
import EndButton from './EndButton';

const StyledCallControlPanel = styled.div`
    display: flex;
    justify-content: center;
    .control-btn {
        cursor: pointer;
        margin: 0;
        margin-left: 2rem;
        margin-right: 2rem;
    }
    .control-camera-btn {
        cursor: pointer;
        margin: 0;
    }
    .control-switch-btn {
        margin: 0 0 0 2rem;
    }
    .icon {
        width: 56px;
        height: 56px;
        color: #fff;
    }
    .disabled-icon {
        color: rgba(255, 255, 255, 0.3);
    }
    &.hidden {
        visibility: hidden;
    }
`;

export interface CallControlPanelProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Call Type (P2P or VRS)
     */
    callType: CallRequestType;
    /**
     * indicates whether user is connected to voice number in VRS call
     */
    voiceNumberConnected?: boolean;
    /**
     * indicates Camera disabled
     */
    cameraDisabled?: boolean;
    /**
     * indicates microphone disabled
     */
    microphoneDisabled?: boolean;
    /**
     * indicates chatbox is visible
     */
    chatVisible?: boolean;
    /**
     * Unread message count in text chat
     */
    chatCount?: number;
    /**
     * method invoked on camera button toggle
     */
    onCameraToggle?: () => void;
    /**
     * method invoked on microphone button toggle
     */
    onMicrophoneToggle?: () => void;
    /**
     * method invoked on chat button toggle
     */
    onChatToggle?: () => void;
    /**
     * method invoked on hangup button click
     */
    onHangup?: () => void;
    /**

     * Auto hide configuration for call control panel
     */
    autoHide?: {
        /**
         * indicates whether to enable auto hide functionality
         */
        enable: boolean;
        /**
         * time in ms until call control panel is shown
         */
        timeout?: number;
    };
    isConnecting: boolean;
    // vi connected
    viConnected?: boolean;
    /**
     * Indicates Volume off/on
     */
    volumeOff?: boolean;
    /**
     * toggle volume on and off
     */
    onVolumeToggle?: () => void;
    endDisabled?: boolean;
}
const ENABLE_CAMERA_OPTION = true; // make this true to show camera in call controll panel
const ENABLE_CHAT_OPTION = false;
const ENABLE_SPEAKER_OPTION = false;

/**
 * Call Control component
 */
const CallControlPanel: React.FC<CallControlPanelProps> = ({
    callType,
    cameraDisabled,
    microphoneDisabled,
    chatVisible = false,
    chatCount = 0,
    onCameraToggle,
    onMicrophoneToggle,
    onChatToggle,
    onHangup,
    className,
    isConnecting,
    volumeOff,
    onVolumeToggle,
    endDisabled,
    ...props
}) => {
    const getMicrophoneToolTipTitle = (): string => {
        return microphoneDisabled ? 'Enable VCO' : 'Disable VCO';
    };

    const renderMicrophoneIcon = (): ReactElement => {
        const ariaLabel = microphoneDisabled ? 'Microphone disabled' : 'Microphone enabled';
        if (microphoneDisabled) {
            return <MicrophoneDisabledIcon className="icon disabled-icon" aria-label={ariaLabel} />;
        }
        return <MicrophoneIcon className="icon" aria-label={ariaLabel} />;
    };

    const renderCameraIcon = (): ReactElement => {
        const ariaLabel = cameraDisabled ? 'Camera disabled' : 'Camera enabled';
        if (cameraDisabled) {
            return <CameraDisabledIcon className="icon disabled-icon" aria-label={ariaLabel} />;
        }
        return <CameraIcon aria-label={ariaLabel} className="icon" />;
    };

    const renderVolumeIcon = (): ReactElement => {
        if (volumeOff) {
            return <VolumeOffIcon className="icon disabled-icon" />;
        }
        return <VolumeUpIcon className="icon" />;
    };

    return (
        <StyledCallControlPanel {...props} className={cx(className)}>
            <div className="call-control">
                <EndButton onClick={onHangup} disabled={endDisabled} className="end-button" />
            </div>
            <Tooltip title={getMicrophoneToolTipTitle()} disableHoverListener={isConnecting}>
                <div id="mic-control" className="control-btn" onClick={onMicrophoneToggle}>
                    {renderMicrophoneIcon()}
                </div>
            </Tooltip>
            {ENABLE_CAMERA_OPTION && (
                <Tooltip
                    title={cameraDisabled ? 'Enable Video' : 'Disable Video'}
                    disableHoverListener={isConnecting}
                >
                    <div id="video-control" className="control-camera-btn" onClick={onCameraToggle}>
                        {renderCameraIcon()}
                    </div>
                </Tooltip>
            )}
            <SwitchDeviceControl className="control-switch-btn" />
            {ENABLE_SPEAKER_OPTION && (
                <Tooltip title={volumeOff ? 'Unmute' : 'Mute'} disableHoverListener={isConnecting}>
                    <div id="volume-control" className="control-btn" onClick={onVolumeToggle}>
                        {renderVolumeIcon()}
                    </div>
                </Tooltip>
            )}

            {ENABLE_CHAT_OPTION && (
                <Tooltip title={chatVisible ? 'Hide Text Chat' : 'Show Text Chat'}>
                    <div id="chat-control" className="control-btn" onClick={onChatToggle}>
                        {chatCount > 0 && <div className="chat-count-badge">{chatCount}</div>}
                        <ChatIcon
                            className={cx('icon chat-icon', { 'disabled-icon': !chatVisible })}
                        />
                    </div>
                </Tooltip>
            )}
        </StyledCallControlPanel>
    );
};

export default CallControlPanel;
